import { Box, Chip, Typography, Divider } from "@mui/material";
import ScienceIcon from "@mui/icons-material/Science";
import InputIcon from "@mui/icons-material/Input";
import SourceIcon from "@mui/icons-material/Source";
import TransformIcon from "@mui/icons-material/Transform";
import StorageIcon from "@mui/icons-material/Storage";
import MergeTypeIcon from "@mui/icons-material/MergeType";
import VerifiedIcon from "@mui/icons-material/VerifiedOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import MyMagicPanelShell from "../shared/MyMagicPanelShell";

const TRUST_COLORS = {
  verified: "#16a34a",
  high: "#16a34a",
  medium: "#d97706",
  low: "#dc2626",
};

const sx = {
  body: { p: 1.5, display: "flex", flexDirection: "column", gap: 1.75 },
  section: { display: "flex", flexDirection: "column", gap: 0.75 },
  sectionHeader: { display: "flex", alignItems: "center", gap: 0.75 },
  sectionTitle: { fontSize: 12, fontWeight: 700, color: "text.primary", textTransform: "uppercase", letterSpacing: "0.05em" },
  list: { display: "flex", flexDirection: "column", gap: 0.5, pl: 0.5 },
  row: { display: "flex", alignItems: "flex-start", gap: 0.75 },
  bullet: { width: 4, height: 4, borderRadius: "50%", backgroundColor: "#cbd5e1", mt: "8px", flexShrink: 0 },
  rowMain: { fontSize: 13, color: "text.primary", lineHeight: 1.45 },
  rowNote: { fontSize: 11, color: "text.secondary", lineHeight: 1.4 },
  empty: { py: 4, display: "flex", justifyContent: "center" },
  emptyText: { color: "text.secondary", fontSize: 13 },
  trustChip: (color) => ({
    height: 18, fontSize: 10, fontWeight: 700, ml: 0.5,
    backgroundColor: `${color}1a`, color, border: `1px solid ${color}66`,
    "& .MuiChip-label": { px: 0.75 }, textTransform: "uppercase", letterSpacing: "0.04em",
  }),
  code: { fontFamily: "Menlo, monospace", fontSize: 12, color: "#0f172a", backgroundColor: "#f1f5f9", px: 0.5, py: 0.125, borderRadius: 0.5 },
  summary: { fontSize: 13, color: "text.secondary", lineHeight: 1.5, fontStyle: "italic" },
  scopeRow: { display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 },
  scopeChip: { height: 18, fontSize: 10, fontWeight: 700, "& .MuiChip-label": { px: 0.75 } },
};

const Section = ({ icon: Icon, color, title, children }) => (
  <Box sx={sx.section}>
    <Box sx={sx.sectionHeader}>
      <Icon sx={{ fontSize: 16, color }} />
      <Typography sx={{ ...sx.sectionTitle, color }}>{title}</Typography>
    </Box>
    <Divider sx={{ borderColor: `${color}33` }} />
    <Box sx={sx.list}>{children}</Box>
  </Box>
);

const BulletRow = ({ children }) => (
  <Box sx={sx.row}>
    <Box sx={sx.bullet} />
    <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
  </Box>
);

const EnrichmentRulesDetailPanel = ({ rule, onClose }) => {
  if (!rule) {
    return (
      <MyMagicPanelShell
        onClose={onClose}
        titleContent={
          <Box sx={{ display: "flex", alignItems: "center", flex: 1, gap: 0.5 }}>
            <ScienceIcon sx={{ fontSize: 16, color: "#0ea5e9" }} />
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }}>
              Enrichment Recipe
            </Typography>
          </Box>
        }
      >
        <Box sx={sx.empty}>
          <Typography sx={sx.emptyText}>Select a rule to see its recipe.</Typography>
        </Box>
      </MyMagicPanelShell>
    );
  }

  const accent = rule.accent || "#0ea5e9";

  return (
    <MyMagicPanelShell
      onClose={onClose}
      titleContent={
        <Box sx={{ display: "flex", alignItems: "center", flex: 1, gap: 0.5 }}>
          <ScienceIcon sx={{ fontSize: 16, color: accent }} />
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }}>
            {rule.name}
          </Typography>
        </Box>
      }
      footerLeft={
        <Typography sx={{ color: "text.secondary", fontSize: 11 }}>Scope: {rule.scope}</Typography>
      }
    >
      <Box sx={sx.body}>
        <Box sx={sx.scopeRow}>
          <Chip size="small" label={rule.scope} sx={{ ...sx.scopeChip, backgroundColor: `${accent}1a`, color: accent }} />
        </Box>
        <Typography sx={sx.summary}>{rule.summary}</Typography>

        <Section icon={InputIcon} color={accent} title="Inputs">
          {rule.inputs.map((it, i) => (
            <BulletRow key={i}>
              <Typography sx={sx.rowMain}>
                <Box component="span" sx={{ fontWeight: 600 }}>{it.label}</Box>
                {it.note ? <> — <Box component="span" sx={sx.code}>{it.note}</Box></> : null}
              </Typography>
            </BulletRow>
          ))}
        </Section>

        <Section icon={SourceIcon} color={accent} title="Sources Consulted">
          {rule.sources.map((s, i) => {
            const c = TRUST_COLORS[s.trust] || "#64748B";
            return (
              <BulletRow key={i}>
                <Typography sx={sx.rowMain}>
                  <Box component="span" sx={{ fontWeight: 600 }}>{s.name}</Box>
                  <Chip size="small" label={s.trust} sx={sx.trustChip(c)} />
                </Typography>
                {s.note ? <Typography sx={sx.rowNote}>{s.note}</Typography> : null}
              </BulletRow>
            );
          })}
        </Section>

        <Section icon={TransformIcon} color={accent} title="Transforms Applied">
          {rule.transforms.map((t, i) => (
            <BulletRow key={i}>
              <Typography sx={sx.rowMain}>{t}</Typography>
            </BulletRow>
          ))}
        </Section>

        {rule.dedupe?.length > 0 && (
          <Section icon={MergeTypeIcon} color={accent} title="Dedupe Strategy">
            {rule.dedupe.map((d, i) => (
              <BulletRow key={i}>
                <Typography sx={sx.rowMain}>{d}</Typography>
              </BulletRow>
            ))}
          </Section>
        )}

        <Section icon={StorageIcon} color={accent} title="Fields Written">
          {rule.fieldsWritten.map((f, i) => (
            <BulletRow key={i}>
              <Typography sx={sx.code}>{f}</Typography>
            </BulletRow>
          ))}
        </Section>

        <Section icon={VerifiedIcon} color={accent} title="Confidence Taxonomy">
          {rule.confidence.map((c, i) => (
            <BulletRow key={i}>
              <Typography sx={sx.rowMain}>
                <Chip size="small" label={c.label} sx={sx.trustChip(c.color)} />
                <Box component="span" sx={{ ml: 0.75 }}>{c.meaning}</Box>
              </Typography>
            </BulletRow>
          ))}
        </Section>

        {rule.openIssues?.length > 0 && (
          <Section icon={WarningAmberIcon} color="#d97706" title="Open Issues">
            {rule.openIssues.map((o, i) => (
              <BulletRow key={i}>
                <Typography sx={sx.rowMain}>{o}</Typography>
              </BulletRow>
            ))}
          </Section>
        )}
      </Box>
    </MyMagicPanelShell>
  );
};

export default EnrichmentRulesDetailPanel;
